/**
 * AuthContext.tsx
 * 
 * Simplified authentication context that focuses on state management
 * Business logic is moved to custom hooks
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { User } from '../types'

// Development bypass - set to true to accept any email/password
const DEV_BYPASS_AUTH = true

// Types
interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  redirectTo: string | null
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: { user: User; token: string } }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_REDIRECT'; payload: string | null }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: false,
  error: null,
  redirectTo: null,
}

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_USER':
      localStorage.setItem('token', action.payload.token)
      return { 
        ...state, 
        user: action.payload.user, 
        token: action.payload.token, 
        isLoading: false, 
        error: null 
      }
    case 'SET_ERROR':
      return { ...state, isLoading: false, error: action.payload }
    case 'LOGOUT':
      localStorage.removeItem('token')
      return { ...state, user: null, token: null, error: null, redirectTo: null }
    case 'SET_REDIRECT':
      return { ...state, redirectTo: action.payload }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

// Context
interface AuthContextType {
  state: AuthState
  dispatch: React.Dispatch<AuthAction>
  login: (email: string, password: string) => Promise<void>
  register: (userData: { name: string; email: string; password: string; userType: 'individual' | 'enterprise' }) => Promise<void>
  logout: () => void
  handlePostLoginRedirect: (userRole?: string) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (token && userData && !state.user) {
      // Only restore user if no user is currently set
      console.log('🔄 Restoring user from token:', token)
      
      try {
        const user = JSON.parse(userData)
        
        // Convert backend user data to frontend User type
        const userType: 'individual' | 'enterprise' = user.user_type === 'enterprise' ? 'enterprise' : 'individual'
        const restoredUser: User = {
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.user_type === 'enterprise' ? 'business' : 'user', // Map user_type to role
          user_type: userType, // Properly typed user_type
          org_id: user.org_id,
          credits: 100, // Default values
          balance: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          profile: {
            avatar: user.user_type === 'enterprise' ? '/avatars/business.jpg' : '/avatars/user.jpg',
            bio: user.user_type === 'enterprise' ? 'Enterprise Business Account' : 'User',
            website: user.user_type === 'enterprise' ? 'https://company.com' : 'https://example.com',
            location: user.user_type === 'enterprise' ? 'Enterprise City' : 'User City'
          }
        }
        
        dispatch({
          type: 'SET_USER',
          payload: { user: restoredUser, token: token },
        })
      } catch (error) {
        console.error('❌ Error parsing user data:', error)
        // Fallback to mock data if parsing fails
        const isBusinessToken = token.startsWith('business-token-')
        
        const mockUser = {
          id: isBusinessToken ? 'business-1' : '1',
          email: isBusinessToken ? 'business@demo.com' : 'user@example.com',
          name: isBusinessToken ? 'Business Admin' : 'User',
          role: (isBusinessToken ? 'business' : 'user') as 'business' | 'user',
          user_type: (isBusinessToken ? 'enterprise' : 'individual') as 'individual' | 'enterprise',
          org_id: isBusinessToken ? 'default' : undefined,
          credits: isBusinessToken ? 999999 : 100,
          balance: isBusinessToken ? 10000 : 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          profile: {
            avatar: isBusinessToken ? '/avatars/business.jpg' : '/avatars/user.jpg',
            bio: isBusinessToken ? 'Enterprise Business Account' : 'Test user',
            website: isBusinessToken ? 'https://company.com' : 'https://example.com',
            location: isBusinessToken ? 'Enterprise City' : 'Test City'
          }
        }
        
        dispatch({
          type: 'SET_USER',
          payload: { user: mockUser, token: token },
        })
      }
    }
  }, [state.user])

  const login = async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      const response = await fetch('http://localhost:8000/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // Store token and user data
        localStorage.setItem('token', data.access_token)
        localStorage.setItem('user', JSON.stringify({
          id: data.user_id,
          email: data.email,
          name: data.name,
          user_type: data.user_type,
          org_id: data.org_id
        }))
        
        // Convert backend user data to frontend User type
        const user: User = {
          id: data.user_id,
          email: data.email,
          name: data.name,
          role: data.user_type === 'enterprise' ? 'business' : 'user',
          user_type: data.user_type,
          org_id: data.org_id,
          credits: 100, // Default values - could be fetched from user profile
          balance: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          profile: {
            avatar: data.user_type === 'enterprise' ? '/avatars/business.jpg' : '/avatars/user.jpg',
            bio: data.user_type === 'enterprise' ? 'Enterprise Business Account' : 'User',
            website: data.user_type === 'enterprise' ? 'https://company.com' : 'https://example.com',
            location: data.user_type === 'enterprise' ? 'Enterprise City' : 'User City'
          }
        }
        
        dispatch({
          type: 'SET_USER',
          payload: { user: user, token: data.access_token },
        })
      } else {
        throw new Error(data.detail || 'Login failed')
      }
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Login failed'
      })
    }
  }

  const register = async (userData: { name: string; email: string; password: string; userType: 'individual' | 'enterprise' }) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      const response = await fetch('http://localhost:8000/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userData.name,
          email: userData.email,
          password: userData.password,
          user_type: userData.userType
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // Store token and user data
        localStorage.setItem('token', data.access_token)
        localStorage.setItem('user', JSON.stringify({
          id: data.user_id,
          email: data.email,
          name: data.name,
          user_type: data.user_type,
          org_id: data.org_id
        }))
        
        // Convert backend user data to frontend User type
        const user: User = {
          id: data.user_id,
          email: data.email,
          name: data.name,
          role: data.user_type === 'enterprise' ? 'business' : 'user',
          user_type: data.user_type,
          org_id: data.org_id,
          credits: 100, // Default values for new users
          balance: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          profile: {
            avatar: data.user_type === 'enterprise' ? '/avatars/business.jpg' : '/avatars/user.jpg',
            bio: data.user_type === 'enterprise' ? 'Enterprise Business Account' : 'New user',
            website: data.user_type === 'enterprise' ? 'https://company.com' : 'https://example.com',
            location: data.user_type === 'enterprise' ? 'Enterprise City' : 'User City'
          }
        }
        
        dispatch({
          type: 'SET_USER',
          payload: { user: user, token: data.access_token },
        })
      } else {
        throw new Error(data.detail || 'Registration failed')
      }
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Registration failed'
      })
    }
  }

  const logout = () => {
    dispatch({ type: 'LOGOUT' })
  }

  const handlePostLoginRedirect = (userRole?: string) => {
    // This function is deprecated - navigation is now handled by individual pages
    // using React Router's useNavigate hook
    console.log('handlePostLoginRedirect called - navigation should be handled by pages')
  }

  const value = { 
    state, 
    dispatch, 
    login, 
    register,
    logout,
    handlePostLoginRedirect
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Hook
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 